import * as React from "react";

interface PesaJetLogoProps extends React.SVGProps<SVGSVGElement> {
  size?: 'sm' | 'md' | 'lg' | 'xl' | number;
  variant?: 'icon' | 'full';
}

export const PesaJetLogo = ({
  size = 'md',
  variant = 'full',
  className = "",
  ...props
}: PesaJetLogoProps) => {
  // Size mapping (adjusted for better visibility)
  const sizeMap = {
    sm: { width: 160, height: 40 },
    md: { width: 240, height: 60 },
    lg: { width: 300, height: 80 },
    xl: { width: 400, height: 100 },
  };

  const dimensions = typeof size === 'number' 
    ? { width: size * 4.5, height: size } 
    : sizeMap[size];

  // If variant is 'icon', we focus on the first character/mark
  const viewBox = variant === 'icon' ? "24 22 35 48" : "0 0 360 80";
  const finalWidth = variant === 'icon' ? (typeof size === 'number' ? size : dimensions.height) : dimensions.width;
  const finalHeight = typeof size === 'number' ? size : dimensions.height;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      id="Layer_1"
      data-name="Layer 1"
      viewBox={viewBox}
      width={finalWidth}
      height={finalHeight}
      preserveAspectRatio="xMidYMid meet"
      className={`text-pesajet-800 transition-colors duration-300 ${className}`}
      {...props}
    >
      {/* PesaJet Mark/Icon (The first character 'P' or brand mark) */}
      <path
        d="M35.3 24.61a13.62 13.62 0 0 1 9.47 3.27 11 11 0 0 1 3.62 8.53A11.22 11.22 0 0 1 44.77 45a13.38 13.38 0 0 1-9.47 3.33h-5.2V42.9h5.14a6.83 6.83 0 0 0 5-1.76A6.17 6.17 0 0 0 42 36.53a6.35 6.35 0 0 0-1.75-4.68 6.73 6.73 0 0 0-5-1.75h-7.78v33.13h-6.19V24.67h14.08Z"
        fill="currentColor"
      />

      {/* Only show the rest of the logo if variant is 'full' */}
      {variant === 'full' && (
        <>
          {/* Rest of 'PesaJet' text */}
          <path
            d="M56.33 46.17h13A6.86 6.86 0 0 0 67.2 41a7.41 7.41 0 0 0-5.2-1.9 7.89 7.89 0 0 0-6.13 2.69 10 10 0 0 0-2.46 7A9.55 9.55 0 0 0 56 55.87a9.3 9.3 0 0 0 6.9 2.63 12.35 12.35 0 0 0 8.35-3.39l2.92 4.2a16.06 16.06 0 0 1-11.45 4.62 15.16 15.16 0 0 1-11-4.32 14.39 14.39 0 0 1-4.38-10.7 14.74 14.74 0 0 1 4.32-10.69 14.16 14.16 0 0 1 10.52-4.38 12.89 12.89 0 0 1 13.26 13.21A18.38 18.38 0 0 1 75 50.9H56.28ZM101.5 52.19a8.67 8.67 0 0 1-2.69 6.54 12.26 12.26 0 0 1-7.07 3.21v7h-5.67v-7a14.93 14.93 0 0 1-10.69-8l4.27-3.16c2.45 3.92 5.66 5.91 9.58 5.91a8 8 0 0 0 4.44-1.17 4.06 4.06 0 0 0 .46-6.31 15.69 15.69 0 0 0-3.85-2.16l-.64-.3 2.22-5.08 1 .35c5.84 2 8.7 5.49 8.7 10Zm-15.31-6.78a15.49 15.49 0 0 1-6.14-3.8 8.43 8.43 0 0 1-2.33-6.07 8.25 8.25 0 0 1 2.33-6 11.58 11.58 0 0 1 6.14-3.22v-6.91h5.67v6.9a12.5 12.5 0 0 1 8.93 6.48L96.59 36q-2.9-4.38-7.19-4.38a7.57 7.57 0 0 0-3.85 1 3.17 3.17 0 0 0-1.64 2.93 3.73 3.73 0 0 0 1 2.62 9.28 9.28 0 0 0 3.62 2.17l.82.29-2.17 5.07-.93-.29ZM119.55 51.84h-4.68c-3.38 0-5.08 1.16-5.08 3.5a3.13 3.13 0 0 0 1.46 2.81 6.74 6.74 0 0 0 3.74.93 9.19 9.19 0 0 0 4.56-1.23v4.44a12.26 12.26 0 0 1-6.31 1.52 10.54 10.54 0 0 1-7-2.28 7.42 7.42 0 0 1-2.75-6 7.08 7.08 0 0 1 2.92-6 12.48 12.48 0 0 1 7.72-2.22h5.37Zm-2.92-17.94a12 12 0 0 1 8.59 3.1c2.16 2 3.21 5 3.21 8.88v17.35h-6.19v-17.7a5.52 5.52 0 0 0-6.08-6.14c-2.86 0-5.49 1.4-7.89 4.27L104.71 40a13.92 13.92 0 0 1 12.09-6.1ZM142 63.64q0 5.34-3 8.06a11.57 11.57 0 0 1-8.12 2.75 14.9 14.9 0 0 1-4.38-.65v-6.6a19.41 19.41 0 0 0 2.39.18 6.09 6.09 0 0 0 3.92-1.06 4.15 4.15 0 0 0 1.28-3.44V34.31H142Zm-3.91-35a5 5 0 0 1-3.57-1.4 4.55 4.55 0 0 1-1.46-3.45 4.64 4.64 0 0 1 1.52-3.5 5.11 5.11 0 0 1 3.62-1.4 5 5 0 0 1 3.57 1.4 4.58 4.58 0 0 1 1.46 3.5 4.55 4.55 0 0 1-1.46 3.45 5 5 0 0 1-3.57 1.4ZM156.3 45.94h10.81a5.58 5.58 0 0 0-1.93-4.27 6.91 6.91 0 0 0-9.58.76 9.39 9.39 0 0 0-2 6.31 8.29 8.29 0 0 0 2.23 6.26 8.54 8.54 0 0 0 6.17 2.21 13 13 0 0 0 8.06-2.92l3.28 5.37A17.67 17.67 0 0 1 161.79 64a15.53 15.53 0 0 1-11.33-4.38A14.58 14.58 0 0 1 146 48.8a15 15 0 0 1 15.19-15.13 13.1 13.1 0 0 1 9.81 3.85 13.24 13.24 0 0 1 3.8 9.58 18.64 18.64 0 0 1-.53 4.33H156.3ZM188 34.31h8.18V41h-20.6v-6.69h4.55V27H188Zm.06 18a4.25 4.25 0 0 0 1.28 3.51 6.49 6.49 0 0 0 4.09 1.05c.47 0 1.7-.06 3.74-.23v6.6a22 22 0 0 1-5.43.7c-3.68 0-6.54-.94-8.59-2.75s-3-4.55-3-8.18v-9.53H188ZM214.32 10.15h1.38v73.02h-1.38z"
        fill="currentColor"
      />
      <text
        style={{
          fontSize: "16.84px",
          fontFamily: "Inter, sans-serif",
          fontWeight: 700,
          fill: "currentColor",
        }}
        transform="translate(231.2 31.84)"
      >
        {"FASTEST"}
        <tspan x={0} y={20.21}>
          {"PAYMENTS"}
        </tspan>
        <tspan x={0} y={40.41}>
          {"SOLUTION"}
        </tspan>
      </text>
    </>
  )}
</svg>
  );
};
